/**
 * Lab Sportsbook `getMemberBets` → Strix `POST /v1/bets/getMemberBets`.
 */

export type MemberBetTime = 'Current' | 'Past'
export type MemberBetType = 'Matched' | 'Unmatched'

export type MemberBetSource = 'sportsbook' | 'premium-bm'

/** Wire request — Lab SportBook / Artemis Premiumbook getMemberBets body. */
export interface GetMemberBetsRequest {
  userId: string
  accessLevel: string
  fromDate: string
  toDate: string
  time: MemberBetTime
  betType: MemberBetType
  /** Artemis Premiumbook Past flag (Lab `previousData`). */
  previousData?: boolean
  source?: MemberBetSource
  page: number
  maxrow: number
  game?: string
  subGame?: string
  eventName?: string
  marketName?: string
  marketUniqueId?: string
  /** Lab Artemis: All | ArtemisBookmaker | ArtemisFancy */
  marketType?: string
  stakeFrom?: number
  stakeTo?: number
  /** Lab ui-select selection — object with `_id` / `username`, or username string. */
  name?: string | { _id?: string; id?: string; username?: string }
  userName?: string
  /** Staff Market access — Lab `profile.bookmakerSessionFlag`. */
  bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
}

/** Lab `formData.name` after ui-select — prefer object so bet-service can id-match. */
export function memberBetNameFilter(
  username: string | null | undefined,
  id?: string | null,
): GetMemberBetsRequest['name'] | undefined {
  const u = username?.trim()
  if (!u) return undefined
  const memberId = id?.trim()
  if (memberId) return { _id: memberId, username: u }
  return { username: u }
}

/** Skip Lab "All" / empty filter tokens before POST. */
export function omitAllFilter(value: string | null | undefined): string | undefined {
  const v = value?.trim()
  if (!v || v.toLowerCase() === 'all') return undefined
  return v
}

/** Wire row from bet-service. */
export interface MemberBetListApiRow {
  betId: string
  userName: string
  memberId?: string
  selectionName: string
  marketName: string
  event: string
  eventType?: string
  type: string
  stake: number
  betRate: number
  runs: number | null
  averagePriceMatched: number
  matchedAmt: number
  unMatchedAmt: number
  status: string
  betWinStatus?: string
  IP?: string
  currencyType?: string
  marketId?: string
  placedDate: string | null
  matchedTime: string | null
  betType?: string
  isBookmaker?: boolean
}

export interface GetMemberBetsResponse {
  results: MemberBetListApiRow[]
  count: number
  maxRow: number
}

/** Console row for the SportBook Bet List table. */
export interface MemberSportBookBet {
  id: string
  memberId: string
  userName: string
  placedAt: string | null
  matchedAt: string | null
  eventName: string
  eventType: string
  marketId: string
  marketName: string
  selection: string
  side: 'back' | 'lay'
  oddsPlaced: number
  oddsMatched: number
  runs: number | null
  stake: number
  matched: number
  unmatched: number
  status: string
  betWinStatus: string
  ip: string
  currencyType: string
  betType: string
  isBookmaker: boolean
  isLineLadder: boolean
}

export function toMemberSportBookBet(row: MemberBetListApiRow): MemberSportBookBet {
  const marketName = row.marketName ?? ''
  const betType = row.betType ?? ''
  const isLineLadder =
    betType.toLowerCase() === 'lineladder' || /line\s*session|overs?\s*line|ball\s*line/i.test(marketName)
  return {
    id: row.betId,
    memberId: row.memberId ?? '',
    userName: row.userName ?? '',
    placedAt: row.placedDate,
    matchedAt: row.status === 'EXECUTION_COMPLETE' || row.matchedTime ? row.matchedTime : null,
    eventName: row.event ?? '',
    eventType: row.eventType ?? '',
    marketId: row.marketId ?? '',
    marketName,
    selection: row.selectionName ?? '',
    side: String(row.type ?? '').toUpperCase() === 'LAY' ? 'lay' : 'back',
    oddsPlaced: Number(row.betRate) || 0,
    oddsMatched: Number(row.averagePriceMatched) || 0,
    runs: row.runs == null ? null : Number(row.runs),
    stake: Number(row.stake) || 0,
    matched: Number(row.matchedAmt) || 0,
    unmatched: Number(row.unMatchedAmt) || 0,
    status: row.status ?? '',
    betWinStatus: row.betWinStatus ?? '',
    ip: row.IP ?? '',
    currencyType: row.currencyType ?? 'INR',
    betType,
    isBookmaker: Boolean(row.isBookmaker) || marketName.toLowerCase().includes('bookmaker'),
    isLineLadder,
  }
}

/** Lab Profit/Liability cell (Current vs Past). */
export function memberBetProfitLiability(
  bet: MemberSportBookBet,
  time: MemberBetTime,
): number {
  const matched = bet.matched
  const odds = bet.isLineLadder ? bet.oddsPlaced : bet.oddsMatched
  const openPl = (odds - 1) * matched
  if (time !== 'Past') return openPl

  const win = bet.betWinStatus.toUpperCase()
  if (win === 'LOST') {
    return bet.side === 'back' ? bet.stake : openPl
  }
  if (win === 'WON') {
    return bet.side === 'lay' ? bet.stake : openPl
  }
  return 0
}

export function dayStartIso(day: string): string {
  return `${day.slice(0, 10)}T00:00:00.000Z`
}

export function dayEndIso(day: string): string {
  return `${day.slice(0, 10)}T23:59:59.999Z`
}
